// ** React Imports
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// ** Component Imports
import BlankLayout from "../../../@core/layouts/BlankLayout";

// ** Hooks
import { useAuth } from "../../../hooks/useAuth";

// ** Util Import
import getHomeRoute from "../../../layouts/components/acl/getHomeRoute";
import { AbilityContext } from "../../../layouts/components/acl/Can";
import { buildAbilityFor } from "../../../configs/acl";
import Spinner from "../spinner";

const AclGuard = ({
  aclAbilities,
  children,
  guestGuard = false,
  authGuard = true,
}) => {
  // ** Hooks
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ** State
  const [ability, setAbility] = useState(null);

  useEffect(() => {
    if (!guestGuard && location.pathname === "/") {
      const homeRoute = getHomeRoute(auth.user);
      navigate(homeRoute);
    }
  }, [auth.user, guestGuard, location.pathname, navigate]);

  // User is logged in, build ability for the user based on role
  useEffect(() => {
    if (!ability) {
      setAbility(
        buildAbilityFor(auth.user?.role || "admin", aclAbilities.subject)
      );
    }
  }, [auth.user, aclAbilities.subject]);

  // Show spinner while loading permissions
  if (!ability) {
    return <Spinner />;
  }

  // If guest guard is enabled or error pages (404, 500) should bypass checks
  if (
    guestGuard ||
    location.pathname === "/404" ||
    location.pathname === "/500" ||
    !authGuard
  ) {
    if (auth.user && ability) {
      return (
        <AbilityContext.Provider value={ability}>
          {children}
        </AbilityContext.Provider>
      );
    } else {
      return <>{children}</>;
    }
  }

  // Check user permissions and render page
  if (ability && ability.can(aclAbilities.action, aclAbilities.subject)) {
    return (
      <AbilityContext.Provider value={ability}>
        {children}
      </AbilityContext.Provider>
    );
  }

  // Render NotAuthorized if user lacks access
  return (
    <BlankLayout>
      {/* Show NotAuthorized or Spinner */}
      <Spinner />
    </BlankLayout>
  );
};

export default AclGuard;
